import React, { useEffect, useMemo, useRef, useState } from "react";
import { callable } from "../../../firebase.js";
import { useFirestoreDoc } from "../../../lib/useFirestore.js";
import { Crumbs, Back } from "./nav.jsx";
import { pad4, resolveTemplate } from "../lib/cameras.js";
import { cameraThumbUrl, thumbCssRotation } from "../lib/thumbs.js";

const templatesEqual = (a, b) => (
  Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((cam, i) => cam === b[i])
);

/**
 * Screen 4 (v1) — the picker. 6 slots, slot 1 = key photo, a preset per rig,
 * amber = a neighbor substituted for a missing camera (resolveCamera). All of
 * the desktop tool's picking semantics (captures.py), now web + Firestore:
 *
 *   • preset dropdown (sheetsConfig/presets, filtered by rigType)
 *   • Apply to all N in group → stores the 6-camera template on the group,
 *     resolves it per capture, marks the group confirmed
 *   • per-capture override (click thumbnails) + reset-to-template
 *   • missing-camera substitution badged amber
 *   • jump-to-camera
 *
 * Group + per-capture selection persist to the captureSheet doc directly this
 * slice (no callable yet — that's the slice-4 push). Rotations are applied at
 * display time from sheetsConfig/rotations_{rigType}.
 */
export default function Picker({ nav, go }) {
  const { jobId, jobLabel, groupId } = nav;
  const groupPath = jobId && groupId ? `jobs/${jobId}/captureSheet/${groupId}` : null;
  const { data: group, loading } = useFirestoreDoc(groupPath);
  const { data: presetsDoc } = useFirestoreDoc("sheetsConfig/presets");
  const rigType = group?.rigType;
  const { data: rotDoc } = useFirestoreDoc(rigType ? `sheetsConfig/rotations_${rigType}` : null);

  const [capIndex, setCapIndex] = useState(0);
  const [presetName, setPresetName] = useState(null);
  const [jump, setJump] = useState("");
  const [busy, setBusy] = useState(false);
  const gridRef = useRef(null);

  const presets = useMemo(
    () => (presetsDoc?.presets || []).filter((p) => !rigType || p.rigType === rigType),
    [presetsDoc, rigType],
  );
  const rotations = rotDoc || {};

  const captures = group?.captures || [];
  const capture = captures[capIndex] || null;
  useEffect(() => {
    setCapIndex(0);
    setPresetName(null);
  }, [groupId]);

  // The active 6-camera template: the chosen preset if one is selected, else the
  // group's applied template, else the first preset for this rig.
  const activePreset = useMemo(() => {
    if (presetName) return presets.find((p) => p.name === presetName) || null;
    if (group?.appliedCameras) return presets.find((p) => templatesEqual(p.cameras, group.appliedCameras)) || null;
    return presets[0] || null;
  }, [group?.appliedCameras, presetName, presets]);
  const activeTemplate = (presetName ? activePreset?.cameras : null) || group?.appliedCameras || activePreset?.cameras || null;

  // Resolve what this capture shows: an explicit per-capture override wins; else
  // the active template resolved against the cameras this capture actually has.
  const view = useMemo(() => {
    if (!capture) return { selected: [], keyCamera: null, subSlots: new Set(), subNote: null };
    if (capture.hasOverride && (capture.selected?.length || 0) > 0) {
      return { selected: capture.selected, keyCamera: capture.keyCamera, subSlots: new Set(), subNote: null };
    }
    if (!activeTemplate) {
      return { selected: capture.selected || [], keyCamera: capture.keyCamera ?? null, subSlots: new Set(), subNote: null };
    }
    const r = resolveTemplate(activeTemplate, capture.availableCameras || []);
    // Build the "0104 missing → substituted 0105" note for the first substitution.
    let subNote = null;
    if (r.substitutedSlots.size) {
      const i = [...r.substitutedSlots][0];
      subNote = `${pad4(activeTemplate[i])} missing in this capture — substituted ${pad4(r.selected[i])}.`;
    }
    return { selected: r.selected, keyCamera: r.keyCamera, subSlots: r.substitutedSlots, subNote };
  }, [capture, activeTemplate]);

  if (loading || !group) return <div className="empty">Loading group…</div>;

  // Slice 4: every pick write goes through the updateSheetPicks callable (no more
  // direct Firestore writes from the client). The callable validates + persists;
  // the UI just computes the new selection (the same toggle/promote/reset logic).
  const setCapturePicks = async (selected, hasOverride) => {
    if (!capture) return;
    setBusy(true);
    try {
      await callable("updateSheetPicks")({ jobId, groupId, op: "setCapture", captureFolder: capture.folderName, selected, hasOverride });
    } finally { setBusy(false); }
  };

  // Click a thumbnail → toggle it into the current capture's selection (override).
  const toggleCamera = (cam) => {
    const cur = view.selected.slice();
    const at = cur.indexOf(cam);
    let next;
    if (at >= 0) {
      next = cur.filter((c) => c !== cam); // deselect
    } else if (cur.length < 6) {
      next = [...cur, cam]; // fill next slot
    } else {
      next = [...cur.slice(0, 5), cam]; // full → replace last (never the key)
    }
    setCapturePicks(next, true);
  };

  // Promote a filled slot to key (slot 0). Reorder only — same camera set, so
  // the signature is unchanged and the embeds stay valid (C1).
  const promoteKey = (cam) => {
    const cur = view.selected.filter((c) => c !== cam);
    setCapturePicks([cam, ...cur], true);
  };

  const resetToTemplate = () => {
    if (!activeTemplate) return;
    const r = resolveTemplate(activeTemplate, capture.availableCameras || []);
    setCapturePicks(r.selected, false);
  };

  // Apply to all N → store the template on the group (server resolves per capture
  // + confirms via the callable).
  const applyToAll = async () => {
    if (!activeTemplate || activeTemplate.length !== 6) return;
    setBusy(true);
    try {
      await callable("updateSheetPicks")({ jobId, groupId, op: "applyToGroup", template: activeTemplate });
      go("groups");
    } finally { setBusy(false); }
  };

  const onJump = (e) => {
    if (e.key !== "Enter") return;
    const n = parseInt(jump, 10);
    if (!Number.isFinite(n)) return;
    const el = gridRef.current?.querySelector(`[data-cam="${n}"]`);
    if (el) { el.scrollIntoView({ block: "center", behavior: "smooth" }); el.classList.add("sel"); }
  };

  const available = capture?.availableCameras || [];
  const selectedSet = new Set(view.selected);
  const subCamSet = new Set([...view.subSlots].map((i) => view.selected[i]));
  const stamp = capture?.stamp || capture?.folderName || "";

  return (
    <>
      <Crumbs
        trail={[
          { label: "Sheets", screen: "home", patch: { jobId: null } },
          { label: jobLabel, screen: "job" },
          { label: "Capture Sheet", screen: "groups" },
          { label: `${group.label} · capture ${capIndex + 1} of ${captures.length}` },
        ]}
        go={go}
      />
      <Back label="Groups" onClick={() => go("groups")} />

      <div className="qhead">
        Pick 6 cameras
        <span className="d">{capture?.sessionName} · {stamp} · {available.length} cameras</span>
      </div>

      <div className="card" style={{ padding: "10px 12px" }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12, flexWrap: "wrap" }}>
          <span style={{ color: "var(--s-muted)" }}>Preset:</span>
          <select
            className="numfield"
            style={{ width: "auto" }}
            value={activePreset?.name || ""}
            onChange={(e) => setPresetName(e.target.value)}
          >
            {presets.length === 0 && <option value="">no preset for {rigType}</option>}
            {presets.map((p) => <option key={p.name} value={p.name}>{p.name}</option>)}
          </select>
          {capture?.hasOverride && (
            <button type="button" className="btnS" style={{ padding: "4px 10px", fontSize: 11 }} onClick={resetToTemplate}>
              Reset to template
            </button>
          )}
          <button
            type="button"
            className="btnT"
            style={{ marginLeft: "auto" }}
            disabled={busy || !activeTemplate || activeTemplate.length !== 6}
            onClick={applyToAll}
          >
            Apply to all {captures.length} in group
          </button>
        </div>

        <div className="slots">
          {Array.from({ length: 6 }).map((_, i) => {
            const cam = view.selected[i];
            if (cam == null) return <span key={i} className="slot" />;
            const isSub = view.subSlots.has(i);
            return (
              <button
                key={i}
                type="button"
                className={`slot full${isSub ? " sub" : ""}`}
                title={i === 0 ? "Key photo" : "Click to make this the key photo"}
                onClick={() => promoteKey(cam)}
              >
                <span
                  className="slot-img"
                  style={{
                    backgroundImage: `url("${cameraThumbUrl(capture, cam, rotations[cam] || 0)}")`,
                    transform: `rotate(${thumbCssRotation(capture, rotations[cam] || 0)}deg)`,
                  }}
                />
                {i === 0 && <span className="key">KEY</span>}
                <span className="cam">{pad4(cam)}</span>
              </button>
            );
          })}
        </div>

        {capture?.hasOverride
          ? <div style={{ fontSize: 10.5, color: "var(--s-muted)" }}>Custom picks for this capture · {view.selected.length}/6</div>
          : view.subNote
            ? <div style={{ fontSize: 10.5, color: "#9A6A08" }}>⚠ {view.subNote} Tap a thumbnail to change.</div>
            : <div style={{ fontSize: 10.5, color: "var(--s-muted)" }}>From preset · {view.selected.length}/6</div>}
      </div>

      <div className="thumbgrid" ref={gridRef}>
        {available.map((cam) => {
          const sel = selectedSet.has(cam);
          const slotIdx = view.selected.indexOf(cam);
          const isSub = subCamSet.has(cam);
          const n = slotIdx === 0 ? "KEY" : sel ? String(slotIdx + 1) : "";
          return (
            <button
              key={cam}
              type="button"
              data-cam={cam}
              className={`thumb${sel ? " sel" : ""}${isSub ? " sub" : ""}`}
              data-n={n}
              title={`Camera ${pad4(cam)}`}
              onClick={() => toggleCamera(cam)}
            >
              <span
                className="thumb-img"
                style={{
                  backgroundImage: `url("${cameraThumbUrl(capture, cam, rotations[cam] || 0)}")`,
                  transform: `rotate(${thumbCssRotation(capture, rotations[cam] || 0)}deg)`,
                }}
              />
            </button>
          );
        })}
      </div>

      <div style={{ fontSize: 11, color: "var(--s-muted)", marginTop: 6, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <span>{available.length} cameras</span>
        <span>jump to camera
          <input
            className="numfield"
            style={{ width: 56, marginLeft: 6 }}
            value={jump}
            onChange={(e) => setJump(e.target.value)}
            onKeyDown={onJump}
            placeholder="__"
          />
        </span>
        <span style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
          <button type="button" className="btnS" style={{ padding: "4px 10px", fontSize: 11 }}
            disabled={capIndex === 0} onClick={() => setCapIndex((i) => Math.max(0, i - 1))}>‹ prev</button>
          <button type="button" className="btnS" style={{ padding: "4px 10px", fontSize: 11 }}
            disabled={capIndex >= captures.length - 1} onClick={() => setCapIndex((i) => Math.min(captures.length - 1, i + 1))}>next ›</button>
        </span>
      </div>
    </>
  );
}
